import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Users, TrendingUp, Shield, BarChart } from 'lucide-react';
import { experienceHighlights, sectors } from '../mock/data';

const Experience = () => {
  return (
    <div className="dark-theme pt-24">
      {/* Hero Section */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-16">
            <h1 className="display-large text-white mb-6">
              Proven Experience.{' '}
              <span className="text-bluemp-400">Trusted Delivery.</span>
            </h1>
            <p className="body-large text-gray-300 max-w-3xl mx-auto mb-8">
              We deliver data-driven AI solutions with measurable results across financial services, insurance, and communications sectors. Our track record speaks to our commitment to excellence and integrity.
            </p>
          </div>
        </div>
      </section>

      {/* Key Metrics */}
      <section className="py-20 bg-gradient-to-b from-black to-gray-900">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="display-medium text-white mb-6">
              Results That Matter
            </h2>
            <p className="body-large text-gray-300 max-w-2xl mx-auto">
              Our impact is measured in real business outcomes and sustainable value creation.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {experienceHighlights.map((highlight, index) => (
              <div key={index} className="text-center group dark-hover p-8 rounded-lg bg-gray-900/50 backdrop-blur-sm border border-bluemp-400/20">
                <div className="display-large text-bluemp-400 mb-4">{highlight.metric}</div>
                <div className="body-medium text-gray-300">{highlight.description}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Detailed Impact */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
            <div>
              <h2 className="display-medium text-white mb-8">
                Measurable Business Impact
              </h2>
              
              <div className="space-y-8">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-bluemp-400/20 rounded-lg flex items-center justify-center flex-shrink-0">
                    <TrendingUp size={24} className="text-bluemp-400" />
                  </div>
                  <div>
                    <h3 className="heading-3 text-white mb-2">Revenue Contribution</h3>
                    <p className="body-medium text-gray-300">Supported multi-million-rand revenue contributions through improved customer engagement and operational efficiency across client organizations.</p>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-bluemp-400/20 rounded-lg flex items-center justify-center flex-shrink-0">
                    <Users size={24} className="text-bluemp-400" />
                  </div>
                  <div>
                    <h3 className="heading-3 text-white mb-2">Customer Reach</h3>
                    <p className="body-medium text-gray-300">Enabled engagement with over 10 million customers through intelligent contactability and campaign optimization solutions.</p>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-bluemp-400/20 rounded-lg flex items-center justify-center flex-shrink-0">
                    <Shield size={24} className="text-bluemp-400" />
                  </div>
                  <div>
                    <h3 className="heading-3 text-white mb-2">Compliance Excellence</h3>
                    <p className="body-medium text-gray-300">Delivered ethical tracing and compliance solutions with 100% audit trail transparency and regulatory adherence.</p>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-bluemp-400/20 rounded-lg flex items-center justify-center flex-shrink-0">
                    <BarChart size={24} className="text-bluemp-400" />
                  </div>
                  <div>
                    <h3 className="heading-3 text-white mb-2">Analytics Intelligence</h3>
                    <p className="body-medium text-gray-300">Consolidated analytics for broker intelligence, providing 360° visibility into client relationships and portfolio performance.</p>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-gray-900/50 backdrop-blur-sm p-8 rounded-lg border border-bluemp-400/20">
              <h3 className="heading-2 text-bluemp-400 mb-6">Key Achievements</h3>
              
              <ul className="space-y-4 mb-8">
                <li className="flex items-start gap-3">
                  <div className="w-2 h-2 bg-bluemp-400 rounded-full mt-3 flex-shrink-0"></div>
                  <span className="body-medium text-gray-300">Improved communication efficiency by 40% through intelligent contactability solutions</span>
                </li>
                <li className="flex items-start gap-3">
                  <div className="w-2 h-2 bg-bluemp-400 rounded-full mt-3 flex-shrink-0"></div>
                  <span className="body-medium text-gray-300">Reduced operational costs by 35% through process automation and optimization</span>
                </li>
                <li className="flex items-start gap-3">
                  <div className="w-2 h-2 bg-bluemp-400 rounded-full mt-3 flex-shrink-0"></div>
                  <span className="body-medium text-gray-300">Enhanced compliance transparency with complete audit trails and reporting</span>
                </li>
                <li className="flex items-start gap-3">
                  <div className="w-2 h-2 bg-bluemp-400 rounded-full mt-3 flex-shrink-0"></div>
                  <span className="body-medium text-gray-300">Delivered measurable, sustained business value across all client engagements</span>
                </li>
              </ul>

              <div className="border-t border-bluemp-400/20 pt-6">
                <h4 className="heading-3 text-white mb-4">Industries Served</h4>
                <div className="flex flex-wrap gap-2">
                  {sectors.map((sector, index) => (
                    <span key={index} className="px-3 py-1 bg-bluemp-400/20 text-bluemp-400 rounded text-sm">
                      {sector}
                    </span>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Success Methodology */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="display-medium text-white mb-6">
              Our Success Methodology
            </h2>
            <p className="body-large text-gray-300 max-w-2xl mx-auto">
              Consistent results come from proven processes and unwavering commitment to our core principles.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {[
              {
                title: "Deep Discovery",
                description: "We invest time upfront to truly understand your business, challenges, and objectives before proposing any solutions."
              },
              {
                title: "Ethical Framework",
                description: "Every solution is built with integrity at its core, ensuring transparency, accountability, and responsible AI practices."
              },
              {
                title: "Measurable Outcomes",
                description: "We define clear KPIs and success metrics from day one, ensuring every solution delivers tangible business value."
              },
              {
                title: "Iterative Improvement",
                description: "Continuous monitoring and optimization ensure solutions adapt and improve over time, maximizing long-term ROI."
              },
              {
                title: "Knowledge Transfer", 
                description: "We build internal capability within your team, reducing dependence and ensuring sustainable success."
              },
              {
                title: "Ongoing Partnership",
                description: "Our relationship doesn't end at implementation. We provide ongoing support to ensure continued success."
              }
            ].map((method, index) => (
              <div key={index} className="text-center group dark-hover p-6 rounded-lg bg-gray-900/30 backdrop-blur-sm">
                <h3 className="heading-3 text-white mb-4 group-hover:text-bluemp-400 transition-colors">
                  {method.title}
                </h3>
                <p className="body-small text-gray-300">{method.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-b from-black to-gray-900">
        <div className="max-w-4xl mx-auto text-center px-6 lg:px-8">
          <h2 className="display-medium text-white mb-6">
            Ready to Join Our Success Stories?
          </h2>
          <p className="body-large text-gray-300 mb-8">
            Let us help you achieve measurable AI success with integrity and excellence. Your success story starts with a conversation.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/contact" className="btn-primary">
              Start Your Success Story
              <ArrowRight size={20} />
            </Link>
            <Link to="/solutions" className="btn-secondary">
              Explore Our Solutions
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Experience;